"use client"

import { useActionState, useEffect, useRef } from "react"
import {
  submitContactForm,
  type ContactFormState,
} from "@lib/data/contact"
import Input from "@modules/common/components/input"

type ContactFormProps = {
  captchaQuestion: string
  captchaToken: string
  defaultSubject?: string
}

const fieldClass =
  "w-full rounded-md border border-vx-line bg-white px-3 py-2.5 text-[15px] text-vx-ink placeholder:text-vx-muted focus:outline-none focus:ring-2 focus:ring-vx-accent/30"

export default function ContactForm({
  captchaQuestion,
  captchaToken,
  defaultSubject,
}: ContactFormProps) {
  const [state, formAction, isPending] = useActionState(
    submitContactForm,
    {
      ok: false,
      message: "",
      captchaQuestion,
      captchaToken,
    } satisfies ContactFormState
  )
  const formRef = useRef<HTMLFormElement>(null)

  const question = state.captchaQuestion || captchaQuestion
  const token = state.captchaToken || captchaToken

  useEffect(() => {
    if (state.ok) {
      formRef.current?.reset()
    }
  }, [state.ok])

  return (
    <form
      ref={formRef}
      action={formAction}
      className="flex flex-col gap-3"
      data-testid="contact-form"
    >
      <div className="hidden" aria-hidden="true">
        <label htmlFor="website">Website</label>
        <input
          id="website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <input type="hidden" name="captcha_token" value={token} />

      <div className="grid gap-3 small:grid-cols-2">
        <Input
          label="ชื่อ-นามสกุล"
          name="name"
          required
          autoComplete="name"
          data-testid="contact-name"
        />
        <Input
          label="อีเมล"
          name="email"
          type="email"
          required
          autoComplete="email"
          data-testid="contact-email"
        />
        <Input
          label="เบอร์โทร"
          name="phone"
          type="tel"
          autoComplete="tel"
          data-testid="contact-phone"
        />
        <Input
          label="บริษัท / องค์กร"
          name="company"
          autoComplete="organization"
          data-testid="contact-company"
        />
      </div>

      <Input
        label="หัวข้อ"
        name="subject"
        required
        defaultValue={defaultSubject}
        data-testid="contact-subject"
      />

      <div className="flex w-full flex-col">
        <label
          htmlFor="message"
          className="mb-1 text-sm font-medium text-vx-ink"
        >
          ข้อความ<span className="text-rose-500">*</span>
        </label>
        <textarea
          id="message"
          name="message"
          required
          rows={3}
          minLength={10}
          maxLength={5000}
          placeholder="รายละเอียด เช่น รุ่นสินค้า จำนวน หรือโปรเจกต์เครือข่าย"
          className={fieldClass}
          data-testid="contact-message"
        />
      </div>

      <div className="flex flex-col gap-2 rounded-md border border-vx-line bg-white/90 p-3 small:flex-row small:items-end">
        <div className="flex-1">
          <label
            htmlFor="captcha_answer"
            className="mb-1 block text-sm font-medium text-vx-ink"
          >
            CAPTCHA — ตอบคำถามเพื่อยืนยันว่าไม่ใช่บอท
            <span className="text-rose-500">*</span>
          </label>
          <p className="mb-2 font-mono text-sm text-vx-ink">
            {question} = ?
          </p>
          <input
            id="captcha_answer"
            name="captcha_answer"
            type="text"
            inputMode="numeric"
            required
            autoComplete="off"
            placeholder="คำตอบ"
            className={fieldClass}
            data-testid="contact-captcha"
          />
        </div>
        <button
          type="submit"
          disabled={isPending}
          className="inline-flex h-11 shrink-0 items-center justify-center bg-vx-accent px-6 text-sm font-semibold text-white transition-colors hover:bg-[#0056cc] disabled:cursor-not-allowed disabled:opacity-60"
          data-testid="contact-submit"
        >
          {isPending ? "กำลังส่ง..." : "ส่งข้อความ"}
        </button>
      </div>

      {state.message ? (
        <p
          role="status"
          className={`text-sm font-medium ${
            state.ok ? "text-emerald-700" : "text-rose-600"
          }`}
          data-testid="contact-status"
        >
          {state.message}
        </p>
      ) : null}
    </form>
  )
}
